import React, { useState, useMemo, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { api } from '@libero/core/services/api';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import {
  AlertCircle, Info, Loader2, CheckCircle, Download,
  ChevronRight, ChevronDown, ChevronLeft, Folder,
} from 'lucide-react';

// ---- Types ---------------------------------------------------------------

interface PlentyCategory {
  id: number;
  parentCategoryId: number | null;
  level: number;
  name: string;
  /** false = in Plenty nicht aktiv (Mandant "Standard" nicht angehakt) → ausgegraut. */
  isActiveStandard?: boolean;
}

interface HerstellerOption {
  producerId: number;
  producerName: string;
}

interface SelectionValue {
  id: number;
  name: string;
}

interface KauflandVariant {
  variationId: number;
  itemId: number;
  variantennr: string;
  artikelname: string;
  isMain: boolean;
  isActive: boolean;
  kauflandDE: boolean;
  kauflandBasis: boolean;
  kauflandKategorieId: string | null;
  kauflandKategorieName: string;
}

interface Summary {
  total: number;
  mitKauflandDE: number;
  ohneKauflandDE: number;
  mitKauflandBasis: number;
  ohneKauflandBasis: number;
  mitKategorie: number;
  ohneKategorie: number;
  hauptvarianten: number;
  untervarianten: number;
  katalogStand?: string | null;
}

interface FieldResult {
  geschrieben: number;
  geskippt: number;
  abweichend: number;
  fehler: number;
  fehlerDetails: string[];
}

interface ApplyResult {
  total: number;
  markt: FieldResult;
  property: FieldResult;
  /** Ausgewaehlte IDs die live in Plenty nicht (mehr) gefunden wurden. */
  nichtGefunden?: number;
}

const PAGE_SIZE = 200;

// ---- App ------------------------------------------------------------------

export default function ManualTab() {
  // Kategorien laden
  const [categories, setCategories] = useState<PlentyCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  // Auswahl
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [categorySearch, setCategorySearch] = useState('');

  // Hersteller-Filter (Einzel-Auswahl — Registry kennt nur ein producerId-Gleichheitsfilter)
  const [herstellerOptions, setHerstellerOptions] = useState<HerstellerOption[]>([]);
  const [herstellerLoading, setHerstellerLoading] = useState(false);
  const [selectedProducerId, setSelectedProducerId] = useState<number | null>(null);

  // Selection-Werte fuer Dropdown
  const [selectionValues, setSelectionValues] = useState<SelectionValue[]>([]);

  // Geladene Auswahl (Filter mit dem geladen wurde) — Basis fuer Seiten/IDs
  const [loadedFilter, setLoadedFilter] = useState<{ categoryIds: number[]; producerId: number | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);

  // Seite
  const [pageRows, setPageRows] = useState<KauflandVariant[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);

  // Varianten-Auswahl (variationId-Menge, ueber Seiten hinweg)
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Zielwert-Dropdown
  const [targetSelectionId, setTargetSelectionId] = useState<string | null>(null);

  // Schreib-Schalter
  const [writeMarket, setWriteMarket] = useState(true);
  const [writeProperty, setWriteProperty] = useState(true);

  // Apply / Schreiben
  const [showConfirm, setShowConfirm] = useState(false);
  const {
    status: applyStatus,
    isRunning: applyRunning,
    progress: applyProgress,
    result: applyResult,
    error: applyError,
    startJob,
    reset: resetJob,
  } = useJobStatus(null);

  // ---- Initial: Kategorien laden ------------------------------------------

  useEffect(() => {
    let abort = false;
    setCategoriesLoading(true);
    (async () => {
      try {
        const r = await api.get<{ success: boolean; data: PlentyCategory[] }>(
          '/programmes/kaufland-kategorisierung/categories',
        );
        if (!abort && r.data.success) setCategories(r.data.data);
      } catch (err: any) {
        if (!abort) setCategoriesError(err.response?.data?.error?.message || 'Fehler beim Laden der Kategorien');
      } finally {
        if (!abort) setCategoriesLoading(false);
      }
    })();
    return () => { abort = true; };
  }, []);

  // ---- Initial: Selection-Werte laden (einmalig) --------------------------

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get<{ success: boolean; data: SelectionValue[] }>(
          '/programmes/kaufland-kategorisierung/selections',
        );
        if (r.data.success) setSelectionValues(r.data.data);
      } catch {
        // silent -- Dropdown zeigt dann leere Liste
      }
    })();
  }, []);

  // ---- Hersteller-Optionen bei Kategorie-Auswahl nachladen (debounced) -----

  useEffect(() => {
    if (selectedCategoryIds.size === 0) {
      setHerstellerOptions([]);
      setSelectedProducerId(null);
      return;
    }
    let abort = false;
    setHerstellerLoading(true);
    const timer = setTimeout(async () => {
      try {
        const r = await api.post<{ success: boolean; data: HerstellerOption[] }>(
          '/programmes/kaufland-kategorisierung/hersteller',
          { categoryIds: Array.from(selectedCategoryIds) },
        );
        if (!abort && r.data.success) {
          setHerstellerOptions(r.data.data);
          // Gewaehlter Hersteller kommt in der neuen Auswahl nicht mehr vor -> zuruecksetzen
          setSelectedProducerId(prev =>
            prev !== null && r.data.data.some(h => h.producerId === prev) ? prev : null,
          );
        }
      } catch {
        if (!abort) setHerstellerOptions([]); // silent — Dropdown zeigt dann nur "Alle Hersteller"
      } finally {
        if (!abort) setHerstellerLoading(false);
      }
    }, 400);
    return () => { abort = true; clearTimeout(timer); };
  }, [selectedCategoryIds]);

  // ---- Derived -------------------------------------------------------------

  /** Lookup: parentId → [child nodes]. Nur direkte Kinder, nicht rekursiv. */
  const childrenOf = useMemo(() => {
    const m = new Map<number, PlentyCategory[]>();
    for (const c of categories) {
      const p = c.parentCategoryId ?? 0;
      if (!m.has(p)) m.set(p, []);
      m.get(p)!.push(c);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    return m;
  }, [categories]);

  /** Rekursiv alle Descendant-IDs einer Kategorie. */
  function getAllDescendantIds(rootId: number): number[] {
    const result: number[] = [];
    const stack = [rootId];
    while (stack.length) {
      const id = stack.pop()!;
      const kids = childrenOf.get(id) ?? [];
      for (const k of kids) {
        result.push(k.id);
        stack.push(k.id);
      }
    }
    return result;
  }

  /**
   * Root-Kategorien: alle, deren parentCategoryId NICHT in der geladenen Menge vorkommt.
   * Unabhaengig vom `level`-Feld (unzuverlaessig).
   */
  const rootCategories = useMemo(() => {
    const allIds = new Set(categories.map(c => c.id));
    return categories
      .filter(c => c.parentCategoryId === null || !allIds.has(c.parentCategoryId))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [categories]);

  /** Bei Suche: nur Treffer (alle Levels). */
  const searchHits = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return null;
    return categories
      .filter(c => c.name.toLowerCase().includes(q) || String(c.id) === q)
      .sort((a, b) => a.name.localeCompare(b.name, 'de'))
      .slice(0, 500);
  }, [categories, categorySearch]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // ---- Skip-Logik (inline, kein Import aus Backend) -----------------------

  /**
   * Bestimmt ob eine Variante in der Tabelle als "skip" (Checkbox disabled) gilt
   * und welchen Status-Text sie erhaelt.
   *
   * Beruecksichtigt die Schalter writeMarket / writeProperty.
   * willSkip === true bedeutet: fuer die aktiven Schalter gibt es nichts zu schreiben ODER inaktiv.
   * Die echte Write-Entscheidung trifft das Backend — beim Apply zusaetzlich auf
   * Basis des FRISCHEN Live-Zustands aus Plenty (Registry kann bis ~24h alt sein).
   */
  function computeVariantSkip(v: KauflandVariant): { willSkip: boolean; statusText: string; statusColor: string } {
    if (!v.isActive) {
      return { willSkip: true, statusText: 'inaktiv', statusColor: 'text-gray-500' };
    }

    const marktEligible = writeMarket && !v.kauflandDE;
    const propEligible  = writeProperty && v.kauflandKategorieId === null;

    if (!marktEligible && !propEligible) {
      // Nichts zu schreiben gemaess aktiver Schalter
      if (!writeMarket && !writeProperty) {
        return { willSkip: true, statusText: 'kein Schalter aktiv', statusColor: 'text-gray-400' };
      }
      // Alle aktiven Felder bereits gesetzt
      return { willSkip: true, statusText: 'vollstaendig — skip', statusColor: 'text-gray-400' };
    }

    // Ab hier: willSkip = false (Checkbox aktiv), Status-Text informiert ueber Teilzustand

    if (writeMarket && !writeProperty) {
      // Nur Kanal-Schalter aktiv
      if (!v.kauflandDE) {
        return { willSkip: false, statusText: 'kanal schreiben', statusColor: 'text-green-700' };
      }
      return { willSkip: true, statusText: 'kanal gesetzt — skip', statusColor: 'text-gray-400' };
    }

    if (!writeMarket && writeProperty) {
      // Nur Property-Schalter aktiv
      if (v.kauflandKategorieId === null) {
        if (!targetSelectionId) {
          return { willSkip: false, statusText: '—', statusColor: 'text-gray-300' };
        }
        return { willSkip: false, statusText: 'prop. schreiben', statusColor: 'text-blue-700' };
      }
      const propAbweichend = targetSelectionId && v.kauflandKategorieId !== targetSelectionId;
      if (propAbweichend) {
        return { willSkip: true, statusText: 'prop. abweichend — skip', statusColor: 'text-amber-700' };
      }
      return { willSkip: true, statusText: 'prop. gesetzt — skip', statusColor: 'text-gray-400' };
    }

    // Beide Schalter aktiv
    if (!targetSelectionId) {
      return { willSkip: false, statusText: '—', statusColor: 'text-gray-300' };
    }

    const marktOk = v.kauflandDE;
    const propGesetzt = v.kauflandKategorieId !== null;
    const propKorrekt = v.kauflandKategorieId === targetSelectionId;
    const propAbweichend = propGesetzt && !propKorrekt;

    if (marktOk && !propGesetzt) {
      return { willSkip: false, statusText: 'markt OK — prop. schreiben', statusColor: 'text-blue-700' };
    }
    if (!marktOk && propKorrekt) {
      return { willSkip: false, statusText: 'prop. korrekt — markt schreiben', statusColor: 'text-blue-700' };
    }
    if (!marktOk && propAbweichend) {
      return { willSkip: false, statusText: 'prop. abweichend — nur markt', statusColor: 'text-amber-700' };
    }
    if (!marktOk && !propGesetzt) {
      return { willSkip: false, statusText: 'bereit (beide)', statusColor: 'text-green-700' };
    }

    return { willSkip: false, statusText: 'bereit', statusColor: 'text-green-700' };
  }

  // ---- Kategorie-Auswahl-Aktionen ------------------------------------------

  /** Toggle eines Knotens inkl. aller Nachfahren (cascade, egal welche Ebene). */
  function toggleCategoryWithCascade(id: number) {
    const descendants = getAllDescendantIds(id);
    setSelectedCategoryIds(prev => {
      const next = new Set(prev);
      const targets = [id, ...descendants];
      const allCurrentlyOn = targets.every(t => next.has(t));
      if (allCurrentlyOn) {
        for (const t of targets) next.delete(t);
      } else {
        for (const t of targets) next.add(t);
      }
      return next;
    });
  }

  function toggleExpand(id: number) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  /** "all" / "some" / "none" fuer Tristate-Visualisierung eines beliebigen Knotens. */
  function nodeSelectionState(nodeId: number): 'all' | 'some' | 'none' {
    const ids = [nodeId, ...getAllDescendantIds(nodeId)];
    let on = 0;
    for (const i of ids) if (selectedCategoryIds.has(i)) on++;
    if (on === 0) return 'none';
    if (on === ids.length) return 'all';
    return 'some';
  }

  // ---- Laden (Summary + Seite 1) -------------------------------------------

  const fetchPage = useCallback(async (flt: { categoryIds: number[]; producerId: number | null }, p: number) => {
    setPageLoading(true);
    try {
      const r = await api.post<{ success: boolean; data: { rows: KauflandVariant[]; page: number; total: number } }>(
        '/programmes/kaufland-kategorisierung/variants',
        { categoryIds: flt.categoryIds, producerId: flt.producerId, page: p, pageSize: PAGE_SIZE },
      );
      if (r.data.success) {
        setPageRows(r.data.data.rows);
        setPage(r.data.data.page);
        setTotal(r.data.data.total);
      }
    } catch (err: any) {
      setLoadError(err.response?.data?.error?.message || 'Fehler beim Laden der Seite');
    } finally {
      setPageLoading(false);
    }
  }, []);

  async function handleLoadVariations() {
    if (selectedCategoryIds.size === 0) {
      setLoadError('Mindestens eine Kategorie auswaehlen');
      return;
    }
    const flt = { categoryIds: Array.from(selectedCategoryIds), producerId: selectedProducerId };
    setLoading(true);
    setLoadError(null);
    setSummary(null);
    setPageRows([]);
    setSelected(new Set());
    resetJob();
    try {
      const r = await api.post<{ success: boolean; data: { summary: Summary } }>(
        '/programmes/kaufland-kategorisierung/load',
        { categoryIds: flt.categoryIds, producerId: flt.producerId },
      );
      if (r.data.success) {
        setSummary(r.data.data.summary);
        setLoadedFilter(flt);
        await fetchPage(flt, 1);
      }
    } catch (err: any) {
      setLoadError(err.response?.data?.error?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }

  function goToPage(p: number) {
    if (!loadedFilter) return;
    const target = Math.min(Math.max(1, p), totalPages);
    fetchPage(loadedFilter, target);
  }

  // ---- Selektion -----------------------------------------------------------

  function toggleRow(variationId: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(variationId)) next.delete(variationId); else next.add(variationId);
      return next;
    });
  }

  /** Alle nicht-geskippten Zeilen der AKTUELLEN Seite auswaehlen. */
  function selectPage() {
    setSelected(prev => {
      const next = new Set(prev);
      for (const v of pageRows) {
        if (!computeVariantSkip(v).willSkip) next.add(v.variationId);
      }
      return next;
    });
  }

  /** ALLE passenden Varianten (ueber alle Seiten) auswaehlen — IDs vom Server. */
  async function selectAllMatching() {
    if (!loadedFilter) return;
    setSelectAllLoading(true);
    try {
      const r = await api.post<{ success: boolean; data: { variationIds: number[] } }>(
        '/programmes/kaufland-kategorisierung/variant-ids',
        { categoryIds: loadedFilter.categoryIds, producerId: loadedFilter.producerId },
      );
      if (r.data.success) setSelected(new Set(r.data.data.variationIds));
    } catch (err: any) {
      setLoadError(err.response?.data?.error?.message || 'Fehler beim Auswaehlen aller Treffer');
    } finally {
      setSelectAllLoading(false);
    }
  }

  function selectNone() {
    setSelected(new Set());
  }

  // ---- Export (alle Treffer, seitenweise nachgeladen) ----------------------

  async function exportToExcel() {
    if (!loadedFilter || total === 0) return;
    setExportLoading(true);
    try {
      const all: KauflandVariant[] = [];
      const pages = Math.ceil(total / PAGE_SIZE);
      for (let p = 1; p <= pages; p++) {
        const r = await api.post<{ success: boolean; data: { rows: KauflandVariant[] } }>(
          '/programmes/kaufland-kategorisierung/variants',
          { categoryIds: loadedFilter.categoryIds, producerId: loadedFilter.producerId, page: p, pageSize: PAGE_SIZE },
        );
        if (r.data.success) all.push(...r.data.data.rows);
      }
      const data = all.map(v => {
        const skip = computeVariantSkip(v);
        return {
          'VariantenNr': v.variantennr || `#${v.variationId}`,
          'Artikelname': v.artikelname || '',
          'Typ': v.isMain ? 'Haupt' : 'Sub',
          'Aktiv': v.isActive ? 'Ja' : 'Nein',
          'Kaufland DE': v.kauflandDE ? 'Ja' : 'Nein',
          'Kaufland (Kanal 102.00)': v.kauflandBasis ? 'Ja' : 'Nein',
          'Aktuelle Kategorie': v.kauflandKategorieName,
          'Status': skip.statusText,
          'In Auswahl': selected.has(v.variationId) ? 'Ja' : 'Nein',
          'Plenty Variation-ID': v.variationId,
          'Plenty Item-ID': v.itemId,
        };
      });
      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = [
        { wch: 14 }, { wch: 40 }, { wch: 6 }, { wch: 6 },
        { wch: 12 }, { wch: 24 }, { wch: 24 }, { wch: 10 }, { wch: 16 }, { wch: 14 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Kaufland-Kategorisierung');
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      XLSX.writeFile(wb, `kaufland-kategorisierung_${ts}.xlsx`);
    } catch (err: any) {
      setLoadError(err.response?.data?.error?.message || 'Fehler beim Excel-Export');
    } finally {
      setExportLoading(false);
    }
  }

  // ---- Apply ---------------------------------------------------------------

  async function handleApply() {
    setShowConfirm(false);
    const selectedIds = Array.from(selected);
    if (selectedIds.length === 0) return;
    await startJob('/programmes/kaufland-kategorisierung/apply', {
      selectedVariationIds: selectedIds,
      targetSelectionId: writeProperty ? targetSelectionId : null,
      writeMarket,
      writeProperty,
    });
  }

  // Roots initial aufklappen sobald Kategorien geladen
  useEffect(() => {
    if (rootCategories.length > 0) {
      setExpanded(prev => {
        const next = new Set(prev);
        for (const r of rootCategories) next.add(r.id);
        return next;
      });
    }
  // Nur beim ersten Laden (rootCategories aendert sich nur wenn categories neu geladen)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootCategories.length]);

  /**
   * Rendert einen Baum-Knoten rekursiv.
   * depth bestimmt die Einrueckung (paddingLeft = depth * 16px).
   */
  const renderNode = useCallback((cat: PlentyCategory, depth: number): React.ReactNode => {
    const children = childrenOf.get(cat.id) ?? [];
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(cat.id);
    const state = nodeSelectionState(cat.id);
    const indent = depth * 16;
    const inaktiv = cat.isActiveStandard === false;
    return (
      <div key={cat.id}>
        <div
          className="flex items-center gap-1 px-2 py-1.5 hover:bg-gray-50"
          style={{ paddingLeft: `${8 + indent}px` }}
          title={inaktiv ? 'In Plenty nicht aktiv — Mandant „Standard" ist bei dieser Kategorie nicht angehakt' : undefined}
        >
          <button
            type="button"
            onClick={() => toggleExpand(cat.id)}
            className="p-0.5 text-gray-500 hover:text-[#1A1A1A] flex-shrink-0"
            aria-label={isExpanded ? 'Einklappen' : 'Aufklappen'}
          >
            {!hasChildren
              ? <span className="inline-block w-4" />
              : isExpanded
                ? <ChevronDown size={16} />
                : <ChevronRight size={16} />}
          </button>
          <Folder size={13} className={`flex-shrink-0 ${inaktiv ? 'text-gray-300' : 'text-gray-400'}`} />
          <input
            type="checkbox"
            checked={state === 'all'}
            ref={el => { if (el) el.indeterminate = state === 'some'; }}
            onChange={() => toggleCategoryWithCascade(cat.id)}
            className="ml-1 flex-shrink-0"
          />
          <span
            onClick={() => toggleCategoryWithCascade(cat.id)}
            className={`text-sm cursor-pointer select-none truncate ${depth === 0 ? 'font-medium' : ''} ${inaktiv ? 'text-gray-400' : ''}`}
          >
            {cat.name}{' '}
            <span className={`text-xs font-normal ${inaktiv ? 'text-gray-300' : 'text-gray-400'}`}>
              (ID {cat.id}{hasChildren ? `, ${children.length} Sub` : ''})
            </span>
            {inaktiv && (
              <span className="ml-1 text-xs font-normal text-gray-400 italic">inaktiv</span>
            )}
          </span>
        </div>
        {isExpanded && hasChildren && children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenOf, expanded, selectedCategoryIds]);

  // ---- Render --------------------------------------------------------------

  const targetName = targetSelectionId
    ? selectionValues.find(s => String(s.id) === targetSelectionId)?.name ?? '--'
    : '--';

  const typedResult = applyResult as ApplyResult | null;

  // Grobe Dauer-Schaetzung: Property = 1 Write/Variante, Markt = 1 Write/50er-Batch.
  // Plenty WRITE-Limit ~75/h — bei grossen Auswahlen laeuft der Job im Hintergrund weiter.
  const estWrites = (writeProperty ? selected.size : 0) + (writeMarket ? Math.ceil(selected.size / 50) : 0);
  const writeHours = estWrites > 75 ? Math.ceil(estWrites / 75) : 0;

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Kaufland-Kategorisierung</h1>

      {/* Info-Box */}
      <div className="bg-[#E9FF57]/20 border border-[#E9FF57] rounded-lg p-4 mb-6 flex gap-3 items-start">
        <Info className="text-[#1A1A1A] mt-0.5 flex-shrink-0" size={20} />
        <div className="text-sm text-[#1A1A1A]">
          <p className="font-semibold mb-1">Ablauf</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Kategorie(n) aus dem Kategorie-Baum auswaehlen — der Baum zeigt die aktiven Shop-Bereiche (gleiche Auswahl wie im Marktplatz-Preisupdate; Sub-Kategorien werden automatisch eingeschlossen).</li>
            <li>„Varianten laden" zeigt die Zusammenfassung; die Varianten werden <strong>seitenweise</strong> aus der Artikeldatenbank geladen (auch grosse Auswahlen wie ganze Wurzel-Kategorien).</li>
            <li>Zielwert waehlen — Status-Spalte zeigt was geschrieben wird (Markt, Property oder beides).</li>
            <li>Auswahl per Checkbox, „diese Seite" oder <strong>„alle passenden"</strong> (Testmodus: erst 1–2 auswaehlen), dann „Schreiben" bestaetigen. Der Server prueft jede Variante beim Schreiben <strong>live</strong> und ueberspringt bereits gesetzte/inaktive automatisch.</li>
          </ol>
        </div>
      </div>

      {/* Filter-Card: Kategorie-Baum */}
      <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">
          Kategorien{selectedCategoryIds.size > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({selectedCategoryIds.size} {selectedCategoryIds.size === 1 ? 'Kategorie' : 'Kategorien'} ausgewaehlt)
            </span>
          )}
        </h2>

        {categoriesLoading && (
          <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
            <Loader2 className="animate-spin" size={14} />
            Lade Kategorien...
          </div>
        )}

        {categoriesError && (
          <div className="text-sm text-red-600 flex items-center gap-2 py-2">
            <AlertCircle size={14} />
            {categoriesError}
          </div>
        )}

        {!categoriesLoading && !categoriesError && (
          <>
            {/* Suche */}
            <input
              type="text"
              value={categorySearch}
              onChange={e => setCategorySearch(e.target.value)}
              placeholder="Kategorien durchsuchen (Name oder ID) — leerlassen fuer Tree-Ansicht"
              className="w-full px-3 py-2 border border-[#DAD9D9] rounded text-sm mb-2"
            />

            {/* Tree- oder Such-Ansicht */}
            <div className="max-h-96 overflow-y-auto border border-[#DAD9D9] rounded">
              {searchHits ? (
                // Suche aktiv: flache Liste (Einrueckung per depth aus level-Feld, nur optisch)
                searchHits.length === 0
                  ? <div className="text-sm text-gray-500 p-3">Keine Treffer.</div>
                  : searchHits.map(cat => {
                      const inaktiv = cat.isActiveStandard === false;
                      return (
                        <label
                          key={cat.id}
                          className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm"
                          title={inaktiv ? 'In Plenty nicht aktiv — Mandant „Standard" ist bei dieser Kategorie nicht angehakt' : undefined}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCategoryIds.has(cat.id)}
                            onChange={() => toggleCategoryWithCascade(cat.id)}
                          />
                          <span className={inaktiv ? 'text-gray-400' : ''}>
                            {cat.name} <span className={`text-xs ${inaktiv ? 'text-gray-300' : 'text-gray-400'}`}>(ID {cat.id})</span>
                            {inaktiv && <span className="ml-1 text-xs text-gray-400 italic">inaktiv</span>}
                          </span>
                        </label>
                      );
                    })
              ) : (
                // Tree-Ansicht: rekursiv, beliebige Tiefe
                rootCategories.length === 0
                  ? <div className="text-sm text-gray-500 p-3">Keine Kategorien geladen.</div>
                  : rootCategories.map(root => renderNode(root, 0))
              )}
            </div>

            <p className="text-xs text-gray-500 mt-1">
              Klick auf eine Kategorie waehlt alle Sub-Kategorien automatisch mit aus.
              Chevron aufklappen um Unterebenen sichtbar zu machen und einzeln anzupassen.
              Ausgegraute Kategorien sind in Plenty nicht aktiv (Mandant „Standard" nicht angehakt) —
              sie bleiben auswaehlbar, enthalten aber i.d.R. keine Shop-relevanten Artikel.
            </p>
          </>
        )}

        {/* Hersteller-Filter (Einzel-Auswahl) + "Varianten laden"-Button */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <label className="text-gray-600">Hersteller:</label>
            <select
              value={selectedProducerId ?? ''}
              onChange={e => setSelectedProducerId(e.target.value === '' ? null : Number(e.target.value))}
              disabled={herstellerOptions.length === 0}
              className="px-2 py-2 border border-[#DAD9D9] rounded text-sm max-w-xs"
              title={herstellerOptions.length === 0 ? 'Erst Kategorie(n) auswaehlen' : undefined}
            >
              <option value="">Alle Hersteller</option>
              {herstellerOptions.map(h => (
                <option key={h.producerId} value={h.producerId}>{h.producerName}</option>
              ))}
            </select>
            {herstellerLoading && <Loader2 className="animate-spin text-gray-400" size={14} />}
          </div>
          <button
            onClick={handleLoadVariations}
            disabled={loading || selectedCategoryIds.size === 0}
            className="px-4 py-2 bg-[#E9FF57] text-[#1A1A1A] font-medium rounded hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : null}
            {loading ? 'Lade Varianten...' : 'Varianten laden'}
          </button>
          {loadError && (
            <div className="basis-full text-sm text-red-600 flex items-center gap-1">
              <AlertCircle size={14} /> {loadError}
            </div>
          )}
        </div>
      </div>

      {/* Summary-Cards */}
      {summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
            <SummaryCard label="Varianten gesamt" value={summary.total} color="#3B82F6" />
            <SummaryCard label="Mit Kaufland DE" value={summary.mitKauflandDE} color="#10B981" />
            <SummaryCard label="Ohne Kaufland DE" value={summary.ohneKauflandDE} color="#F59E0B" />
            <SummaryCard label="Mit Kaufland (Kanal)" value={summary.mitKauflandBasis} color="#0EA5E9" />
            <SummaryCard label="Mit Kategorie" value={summary.mitKategorie} color="#10B981" />
            <SummaryCard label="Ohne Kategorie" value={summary.ohneKategorie} color="#F59E0B" />
            <SummaryCard label="Hauptvarianten" value={summary.hauptvarianten} color="#6366F1" />
            <SummaryCard label="Untervarianten" value={summary.untervarianten} color="#8B5CF6" />
          </div>
          {summary.katalogStand && (
            <p className="text-xs text-gray-500 mb-4 -mt-2">
              Katalog-Stand (Artikeldatenbank): {new Date(summary.katalogStand).toLocaleString('de-DE')}.
              Der Kaufland-Ist-Zustand wird beim Schreiben tagesaktuell aus Plenty geprueft.
            </p>
          )}
        </>
      )}

      {/* Zielwert-Card mit Schreib-Schaltern */}
      {summary && total > 0 && (
        <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-4 flex flex-wrap items-start gap-6">
          {/* Schalter */}
          <div className="flex flex-col gap-3 min-w-72">
            <p className="text-sm font-medium text-[#1A1A1A]">Was soll geschrieben werden?</p>
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm">
              <input
                type="checkbox"
                checked={writeMarket}
                onChange={e => setWriteMarket(e.target.checked)}
                className="w-4 h-4"
              />
              <span>Verkaufskanal Kaufland DE (102.01) zuweisen</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm">
              <input
                type="checkbox"
                checked={writeProperty}
                onChange={e => setWriteProperty(e.target.checked)}
                className="w-4 h-4"
              />
              <span>Kaufland.de-Kategorie (Property 107) setzen</span>
            </label>
          </div>

          {/* Zielwert-Dropdown (nur relevant wenn writeProperty aktiv) */}
          <div className={writeProperty ? '' : 'opacity-40 pointer-events-none'}>
            <label className="block text-sm font-medium mb-1">
              Zielwert „Kaufland.de-Kategorie"
            </label>
            <select
              value={targetSelectionId ?? ''}
              onChange={e => setTargetSelectionId(e.target.value || null)}
              disabled={!writeProperty}
              className="px-3 py-2 border border-[#DAD9D9] rounded text-sm min-w-64"
            >
              <option value="">-- Zielwert waehlen --</option>
              {selectionValues.map(s => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Status-Spalte zeigt was fuer jede Variante geschrieben wird.
              Inaktive und vollstaendig gesetzte Zeilen werden uebersprungen.
            </p>
          </div>
        </div>
      )}

      {/* Vorschau-Tabelle */}
      {summary && total > 0 && (
        <div className="bg-white border border-[#DAD9D9] rounded-lg overflow-hidden mb-4">
          {/* Tabellen-Toolbar */}
          <div className="px-4 py-2 bg-gray-50 border-b border-[#DAD9D9] flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={selectPage}
              className="px-3 py-1.5 bg-white border border-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-50"
            >
              Diese Seite auswaehlen
            </button>
            <button
              onClick={selectAllMatching}
              disabled={selectAllLoading}
              className="px-3 py-1.5 bg-white border border-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
              title="Alle passenden Varianten aller Seiten auswaehlen — der Server ueberspringt bereits gesetzte/inaktive beim Schreiben automatisch"
            >
              {selectAllLoading && <Loader2 className="animate-spin" size={14} />} Alle passenden auswaehlen ({total.toLocaleString('de-DE')})
            </button>
            <button
              onClick={selectNone}
              className="px-3 py-1.5 bg-white border border-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-50"
            >
              Auswahl leeren
            </button>
            <span className="text-gray-600 font-medium">
              {selected.size.toLocaleString('de-DE')} ausgewaehlt
            </span>
            <button
              onClick={exportToExcel}
              disabled={exportLoading}
              className="ml-auto px-3 py-1.5 bg-white border border-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
              title="Alle Treffer als Excel exportieren"
            >
              {exportLoading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} Excel-Export
            </button>
          </div>

          {/* Tabelle */}
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto relative">
            {pageLoading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
                <Loader2 className="animate-spin text-[#1A1A1A]" size={24} />
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-left border-b border-[#DAD9D9]">
                  <th className="px-2 py-2 w-10"></th>
                  <th className="px-3 py-2">VariantenNr</th>
                  <th className="px-3 py-2">Artikelname</th>
                  <th className="px-3 py-2 w-20">Typ</th>
                  <th className="px-3 py-2 w-20">Aktiv</th>
                  <th className="px-3 py-2 w-28">Kaufland DE</th>
                  <th className="px-3 py-2 w-32" title="Basis-Verkaufskanal Kaufland (Referrer 102.00) — ueber diesen bleiben Artikel auf Kaufland gelistet, auch ohne Kaufland DE (102.01)">Kaufland (Kanal)</th>
                  <th className="px-3 py-2">Aktuelle Kategorie</th>
                  <th className="px-3 py-2">Zielwert</th>
                  <th className="px-3 py-2 w-44">Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(v => {
                  const skip = computeVariantSkip(v);
                  const isSelected = selected.has(v.variationId);
                  const rowBg = skip.willSkip
                    ? 'bg-gray-50/60'
                    : isSelected
                      ? 'bg-[#E9FF57]/10'
                      : '';
                  return (
                    <tr key={v.variationId} className={`border-b border-gray-100 ${rowBg}`}>
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={skip.willSkip && !isSelected}
                          onChange={() => toggleRow(v.variationId)}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {v.variantennr || `#${v.variationId}`}
                      </td>
                      <td className="px-3 py-2">
                        {v.artikelname || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${v.isMain ? 'bg-indigo-100 text-indigo-800' : 'bg-purple-100 text-purple-800'}`}>
                          {v.isMain ? 'Haupt' : 'Sub'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {v.isActive
                          ? <span className="text-green-700 font-medium">Ja</span>
                          : <span className="text-gray-500">Nein</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {v.kauflandDE
                          ? <span className="text-green-700 font-medium">Ja</span>
                          : <span className="text-red-700">Nein</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {v.kauflandBasis
                          ? <span className="text-green-700 font-medium">Ja</span>
                          : <span className="text-red-700">Nein</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {v.kauflandKategorieName === 'nicht gesetzt'
                          ? <span className="text-gray-400 italic">nicht gesetzt</span>
                          : v.kauflandKategorieName}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {targetSelectionId ? targetName : <span className="text-gray-300">--</span>}
                      </td>
                      <td className={`px-3 py-2 text-xs ${skip.statusColor}`}>
                        {skip.statusText}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination-Leiste */}
          <div className="px-4 py-2 bg-gray-50 border-t border-[#DAD9D9] flex items-center gap-3 text-sm">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || pageLoading}
              className="px-2 py-1 bg-white border border-[#DAD9D9] rounded hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
            >
              <ChevronLeft size={14} /> Zurueck
            </button>
            <span className="text-gray-600">
              Seite {page} / {totalPages} · {total.toLocaleString('de-DE')} Varianten
            </span>
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages || pageLoading}
              className="px-2 py-1 bg-white border border-[#DAD9D9] rounded hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
            >
              Weiter <ChevronRight size={14} />
            </button>
          </div>

          {/* Footer: Schreiben-Button */}
          <div className="px-4 py-3 bg-gray-50 border-t border-[#DAD9D9] flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-gray-700">
              {selected.size.toLocaleString('de-DE')} {selected.size === 1 ? 'Variante' : 'Varianten'} ausgewaehlt
              {writeHours > 0 && (
                <span className="text-amber-700"> · Achtung: Plenty-Write-Limit ~75/h → Schreiben dauert ca. {writeHours} h.</span>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              {(() => {
                const noSwitch = !writeMarket && !writeProperty;
                const missingTarget = writeProperty && !targetSelectionId;
                const noSelection = selected.size === 0;
                const isDisabled = applyRunning || noSelection || noSwitch || missingTarget;
                let hint: string | null = null;
                if (!applyRunning) {
                  if (noSwitch) hint = 'Mindestens einen Schreib-Schalter aktivieren.';
                  else if (missingTarget) hint = 'Zielwert fuer Kategorie waehlen.';
                  else if (noSelection) hint = 'Mindestens eine Variante auswaehlen.';
                }
                return (
                  <>
                    {hint && <p className="text-xs text-amber-700">{hint}</p>}
                    <button
                      onClick={() => setShowConfirm(true)}
                      disabled={isDisabled}
                      className={`px-4 py-2 font-medium rounded flex items-center gap-2 transition-colors
                        ${!isDisabled
                          ? 'bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] cursor-pointer'
                          : 'bg-[#DAD9D9] text-[#1A1A1A] cursor-not-allowed opacity-60'}`}
                    >
                      {applyRunning
                        ? <><Loader2 className="animate-spin" size={16} /> Schreibt...</>
                        : `${selected.size.toLocaleString('de-DE')} ${selected.size === 1 ? 'Variante' : 'Varianten'} in Plenty schreiben`}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Fortschrittsbalken waehrend Job */}
          {applyRunning && (
            <div className="px-4 pb-3">
              <div className="h-2 bg-[#DAD9D9] rounded overflow-hidden">
                <div
                  className="h-2 bg-[#E9FF57] rounded transition-all duration-300"
                  style={{ width: applyProgress ? `${applyProgress.percentage}%` : '10%' }}
                />
              </div>
              {applyProgress?.message && (
                <p className="text-sm mt-1 text-gray-600">{applyProgress.message}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 409 / Fehler-Banner */}
      {applyError && !applyRunning && applyStatus !== 'completed' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={18} />
          <div>
            <p className="font-medium text-red-800">Schreibvorgang fehlgeschlagen</p>
            <p className="text-sm text-red-700 mt-1">{applyError}</p>
            {applyError.toLowerCase().includes('laeuft') || applyError.toLowerCase().includes('running') || applyError.toLowerCase().includes('already') ? (
              <p className="text-xs text-red-600 mt-1">Ein Schreibvorgang laeuft bereits — bitte warten bis er abgeschlossen ist.</p>
            ) : null}
          </div>
        </div>
      )}

      {/* Ergebnis-Panel */}
      {(applyStatus === 'completed' || applyStatus === 'failed') && (
        <div className={`mb-4 rounded-lg border p-4 ${applyStatus === 'completed' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-start gap-3">
            {applyStatus === 'completed'
              ? <CheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
              : <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />}
            <div className="flex-1">
              <h3 className="font-semibold text-[#1A1A1A] mb-3">
                {applyStatus === 'completed' ? 'Schreibvorgang abgeschlossen' : 'Schreibvorgang fehlgeschlagen'}
              </h3>

              {applyStatus === 'completed' && typedResult && (
                <>
                  <p className="text-sm text-gray-700 mb-3">
                    Gesamt verarbeitet: <strong>{typedResult.total.toLocaleString('de-DE')}</strong> Varianten
                    {(typedResult.nichtGefunden ?? 0) > 0 && (
                      <span className="text-amber-700"> · {typedResult.nichtGefunden} in Plenty nicht gefunden (uebersprungen)</span>
                    )}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {/* Markt-Spalte */}
                    <div className="border border-green-200 rounded p-3 bg-white">
                      <div className="font-medium mb-2 text-[#1A1A1A]">Markt Kaufland DE (102.01)</div>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Geschrieben</span>
                          <span className="font-semibold text-green-700">{typedResult.markt.geschrieben.toLocaleString('de-DE')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Uebersprungen</span>
                          <span className="font-semibold text-gray-600">{typedResult.markt.geskippt.toLocaleString('de-DE')}</span>
                        </div>
                        {typedResult.markt.fehler > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Fehler</span>
                            <span className="font-semibold text-red-700">{typedResult.markt.fehler}</span>
                          </div>
                        )}
                      </div>
                      {typedResult.markt.fehlerDetails?.length > 0 && (
                        <details className="mt-2 text-xs">
                          <summary className="cursor-pointer text-red-600">
                            Fehlerdetails ({typedResult.markt.fehlerDetails.length})
                          </summary>
                          <ul className="mt-1 list-disc list-inside text-red-700 space-y-0.5">
                            {typedResult.markt.fehlerDetails.map((e, i) => <li key={i}>{e}</li>)}
                          </ul>
                        </details>
                      )}
                    </div>

                    {/* Property-Spalte */}
                    <div className="border border-green-200 rounded p-3 bg-white">
                      <div className="font-medium mb-2 text-[#1A1A1A]">Property Kaufland.de-Kategorie (107)</div>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Geschrieben</span>
                          <span className="font-semibold text-green-700">{typedResult.property.geschrieben.toLocaleString('de-DE')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Uebersprungen</span>
                          <span className="font-semibold text-gray-600">{typedResult.property.geskippt.toLocaleString('de-DE')}</span>
                        </div>
                        {typedResult.property.abweichend > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Abweichend (skip)</span>
                            <span className="font-semibold text-amber-700">{typedResult.property.abweichend.toLocaleString('de-DE')}</span>
                          </div>
                        )}
                        {typedResult.property.fehler > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Fehler</span>
                            <span className="font-semibold text-red-700">{typedResult.property.fehler}</span>
                          </div>
                        )}
                      </div>
                      {typedResult.property.fehlerDetails?.length > 0 && (
                        <details className="mt-2 text-xs">
                          <summary className="cursor-pointer text-red-600">
                            Fehlerdetails ({typedResult.property.fehlerDetails.length})
                          </summary>
                          <ul className="mt-1 list-disc list-inside text-red-700 space-y-0.5">
                            {typedResult.property.fehlerDetails.map((e, i) => <li key={i}>{e}</li>)}
                          </ul>
                        </details>
                      )}
                    </div>
                  </div>
                </>
              )}

              {applyStatus === 'failed' && applyError && (
                <p className="text-sm text-red-700">{applyError}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bestaetigungs-Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3">Schreiben bestaetigen</h2>

            <p className="text-gray-700 mb-1">
              <strong>{selected.size.toLocaleString('de-DE')}</strong> {selected.size === 1 ? 'Variante wird' : 'Varianten werden'} in Plenty geschrieben.
            </p>
            {writeProperty && (
              <p className="text-gray-700 mb-3">
                Zielwert: <strong>{targetName}</strong>
              </p>
            )}

            {/* Schreib-Zusammenfassung */}
            <div className="bg-gray-50 rounded p-3 mb-3 text-sm space-y-1">
              {writeMarket && (
                <div className="text-gray-600">Markt Kaufland DE (102.01) wird zugewiesen</div>
              )}
              {writeProperty && (
                <div className="text-gray-600">Property Kaufland.de-Kategorie (107) wird gesetzt</div>
              )}
              <div className="text-xs text-gray-500 pt-1">
                Der Server prueft jede Variante <strong>live</strong> in Plenty und ueberspringt
                inaktive sowie bereits gesetzte Felder automatisch (idempotent).
              </div>
            </div>

            {/* WRITE-Limit-Hinweis bei grosser Auswahl */}
            {estWrites > 30 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3 text-xs text-amber-800">
                <strong>WRITE-Limit:</strong> Plenty erlaubt max. 75 Writes/Stunde.
                Bei bis zu {estWrites.toLocaleString('de-DE')} Writes laeuft der Job
                {writeHours > 0 ? ` ca. ${writeHours} h` : ' mehrere Minuten'} im Hintergrund.
              </div>
            )}

            <p className="text-sm text-amber-700 mb-4">
              Achtung: Schreibt direkt in Produktions-Plenty.
              Testmodus = nur die ausgewaehlten {selected.size === 1 ? 'Variante' : 'Varianten'} werden beschrieben.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 bg-white border border-[#DAD9D9] text-[#1A1A1A] rounded hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleApply}
                className="px-4 py-2 bg-[#E9FF57] text-[#1A1A1A] font-medium rounded hover:bg-[#d4ea4d]"
              >
                Jetzt schreiben
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Hilfs-Komponenten ------------------------------------------------------

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-[#DAD9D9] rounded p-3">
      <div className="text-xs text-gray-600">{label}</div>
      <div className="text-xl font-bold" style={{ color }}>{value.toLocaleString('de-DE')}</div>
    </div>
  );
}
